import DOMPurify from 'dompurify'
import { translateDbValue } from '@/utils/dbI18n'

/** Получаем i18n из window.$nuxt */
function getI18n() {
  if (typeof window !== 'undefined' && window.$nuxt?.$i18n) {
    return window.$nuxt.$i18n
  }
  return null
}

/** Локализованная строка через i18n */
function t(key) {
  const i18n = getI18n()
  return i18n?.t(key) || key
}

/** Массив ключей месяцев для i18n */
const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
]

/** Получить название месяца через i18n */
function getMonthName(monthIndex) {
  const key = MONTH_KEYS[monthIndex]
  return t(`pages.calendar.months.${key}`)
}

/**
 * Ключи i18n по типам уведомлений. Храним именно ключи, а не переводы:
 * модуль загружается раньше, чем готов window.$nuxt, и ранний вызов t()
 * вернул бы сам ключ вместо перевода.
 */
const TYPE_LABEL_KEYS = {
  appointment_created: 'pages.notifications.list.types.record_created_2',
  appointment_reschedule_request:
    'pages.notifications.list.types.reschedule_request',
  appointment_cancelled_by_client:
    'pages.notifications.list.types.cancelled_by_client',
  system_update: 'pages.notifications.list.types.system_update',
  maintenance: 'pages.notifications.list.types.maintenance',
}

const APPOINTMENT_BODY_TYPES = new Set([
  'appointment_created',
  'appointment_reschedule_request',
  'appointment_cancelled_by_client',
])

const SOCKET_TOAST_KEYS = {
  appointment_reschedule_request: {
    once: 'pages.notifications.toasts.reschedule_request',
    repeat: 'pages.notifications.toasts.reschedule_request_repeat',
  },
  appointment_cancelled_by_client: {
    once: 'pages.notifications.toasts.cancelled_by_client',
    repeat: 'pages.notifications.toasts.cancelled_by_client',
  },
}

/** Типы, у которых body — HTML из текстового редактора */
export const HTML_BODY_TYPES = new Set(['system_update', 'maintenance'])

export function typeLabel(type) {
  const key = TYPE_LABEL_KEYS[type]
  return key ? t(key) : type
}

export function clientName(payload) {
  return (
    payload?.clientName ||
    t('pages.notifications.list.client_name_not_specified')
  )
}

export function isAppointmentRecordNotification(type) {
  return APPOINTMENT_BODY_TYPES.has(type)
}

export function socketToastText(payload) {
  const keys = SOCKET_TOAST_KEYS[payload?.message?.type]
  if (!keys) return t('layouts.organization.toasts.new_notification')
  return t(payload?.isRepeat ? keys.repeat : keys.once)
}

export function calendarRoute(orgId, payload) {
  const recordId = payload?.recordId
  const departmentId = payload?.departmentId
  if (!recordId || !departmentId || !orgId) return null

  return {
    path: `/org/${orgId}/calendar/${departmentId}`,
    query: {
      recordId: String(recordId),
      openSidePanel: '1',
    },
  }
}

export function displayBody(msg) {
  if (APPOINTMENT_BODY_TYPES.has(msg.type)) {
    const name = clientName(msg.payload)
    const dept = msg.payload?.departmentName
    return dept ? `${name}, ${dept}` : name
  }
  if (HTML_BODY_TYPES.has(msg.type)) {
    return htmlToPlainText(msg.body)
  }
  return msg.body || ''
}

/** Текстовое превью HTML-описания для карточки в списке */
function htmlToPlainText(html) {
  if (!html) return ''
  const text = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  })
  return text.replace(/\s+/g, ' ').trim()
}

/** Форматировать дату в виде "5 янв., 14:30" */
export function formatNotificationDate(dateStr) {
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''

  const day = date.getDate()
  const month = getMonthName(date.getMonth())
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day} ${month}, ${hours}:${minutes}`
}

/** Форматировать дату в виде "5 января 2026, 14:30" */
export function formatNotificationDateFull(dateStr) {
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''

  const day = date.getDate()
  const month = getMonthName(date.getMonth())
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `-${day} ${month} ${year}, ${hours}:${minutes}`
}

/** ФИО сотрудника с должностью: "Мастер: Иванов Иван" */
export function formatEmployeeName(employee) {
  /* Должность из БД может быть ключом перевода (системные роли) — переводим */
  const positionName = translateDbValue(employee?.position?.name)

  return [
    positionName && `${positionName}:`,
    employee?.f_name,
    employee?.m_name,
    employee?.l_name,
  ]
    .filter(Boolean)
    .join(' ')
}
